import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { hostname } from '../../../providers/api';

const BAND_COLORS = {
  high: { backgroundColor: '#d9f2e6', color: '#176b43' },
  medium: { backgroundColor: '#fff0c7', color: '#815c00' },
  low: { backgroundColor: '#fbe0df', color: '#9c2d2a' },
};

const formatNumber = (value, maximumFractionDigits = 2) =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits }).format(value);

const defaultFetch = (...args) => window.fetch(...args);

const buildCollectionQuery = (collections) => {
  const query = new URLSearchParams();
  collections.forEach((collection) => query.append('collection', collection));
  return query.toString();
};

const withQuery = (url, query) => (query ? `${url}?${query}` : url);

const CollectionFilter = ({ loading, options, selected, onChange, onClear }) => (
  <Box marginBottom={3} maxWidth={440}>
    <label htmlFor="circularity-collections">
      <Typography component="span" variant="subtitle2">
        Collections
      </Typography>
    </label>
    <select
      id="circularity-collections"
      multiple
      value={selected}
      onChange={(event) =>
        onChange(
          Array.from(event.target.selectedOptions, (option) => option.value),
        )
      }
      disabled={loading}
      style={{ display: 'block', marginTop: 8, minHeight: 80, width: '100%' }}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    <Box display="flex" alignItems="center" gridGap={12} marginTop={1}>
      <Button size="small" onClick={onClear} disabled={selected.length === 0}>
        All collections
      </Button>
      <Typography color="textSecondary" variant="caption">
        {loading
          ? 'Loading collections...'
          : selected.length === 0
          ? 'Showing all collections'
          : `${selected.length} selected`}
      </Typography>
    </Box>
  </Box>
);

const CircularityStatisticsTable = ({ label, statistics }) => (
  <table
    aria-label={`${label} statistics`}
    style={{ width: '100%', borderCollapse: 'collapse' }}
  >
    <thead>
      <tr>
        <th align="left">Measure</th>
        <th align="right">Value</th>
        <th align="right">SKU count</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style={{ padding: '8px 0' }}>Average</td>
        <td align="right">
          {statistics.average === null
            ? 'N/A'
            : `${formatNumber(statistics.average)} / ${statistics.scale.maximum}`}
        </td>
        <td align="right">—</td>
      </tr>
      <tr>
        <td style={{ padding: '8px 0' }}>Minimum</td>
        <td align="right">
          {statistics.minimum === null
            ? 'N/A'
            : formatNumber(statistics.minimum)}
        </td>
        <td align="right">{statistics.minimumSkuCount}</td>
      </tr>
      <tr>
        <td style={{ padding: '8px 0' }}>Maximum</td>
        <td align="right">
          {statistics.maximum === null
            ? 'N/A'
            : formatNumber(statistics.maximum)}
        </td>
        <td align="right">{statistics.maximumSkuCount}</td>
      </tr>
      <tr>
        <td style={{ padding: '8px 0' }}>
          High (≥ {formatNumber(statistics.highThreshold)})
        </td>
        <td align="right">Threshold</td>
        <td align="right">{statistics.highSkuCount}</td>
      </tr>
    </tbody>
  </table>
);

const CircularityRankingTable = ({ label, rows }) => (
  <table
    aria-label={`${label} circularity articles`}
    style={{ width: '100%', borderCollapse: 'collapse' }}
  >
    <thead>
      <tr>
        <th align="left">Article</th>
        <th align="left">Reference</th>
        <th align="right">MCI</th>
        <th align="right">Band</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.articleId}>
          <td style={{ padding: '8px 0' }}>{row.articleName || '—'}</td>
          <td>{row.articleReference || '—'}</td>
          <td align="right">{formatNumber(row.percentage)}%</td>
          <td align="right">
            {row.band.charAt(0).toUpperCase() + row.band.slice(1)}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CircularityMetricsSection = ({
  projectId,
  accessToken,
  fetchImpl = defaultFetch,
}) => {
  const [requestVersion, setRequestVersion] = React.useState(0);
  const [state, setState] = React.useState({ status: 'loading' });
  const [distribution, setDistribution] = React.useState({ status: 'idle' });
  const [statistics, setStatistics] = React.useState({ status: 'idle' });
  const [ranking, setRanking] = React.useState({ status: 'idle' });
  const [collectionOptions, setCollectionOptions] = React.useState([]);
  const [selectedCollections, setSelectedCollections] = React.useState([]);
  const [collectionOptionsLoading, setCollectionOptionsLoading] = React.useState(
    true,
  );
  const collectionQuery = React.useMemo(
    () => buildCollectionQuery(selectedCollections),
    [selectedCollections],
  );

  React.useEffect(() => {
    let active = true;
    setState({ status: 'loading' });

    fetchImpl(
      withQuery(
        `${hostname}/dashboard/${projectId}/circularity-summary`,
        collectionQuery,
      ),
      { headers: { authorization: `Bearer ${accessToken}` } },
    )
      .then(async (response) => {
        if (response.status === 404) return { status: 'not-calculated' };
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return data.hasData
          ? { status: 'success', data }
          : { status: 'empty' };
      })
      .then((nextState) => {
        if (active) setState(nextState);
      })
      .catch(() => {
        if (active) setState({ status: 'error' });
      });

    return () => {
      active = false;
    };
  }, [accessToken, collectionQuery, fetchImpl, projectId, requestVersion]);

  React.useEffect(() => {
    let active = true;
    setCollectionOptionsLoading(true);
    fetchImpl(`${hostname}/articles/collections/${projectId}/filter-options`, {
      headers: { authorization: `Bearer ${accessToken}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const body = await response.json();
        return Array.isArray(body.data) ? [...new Set(body.data)] : [];
      })
      .then((options) => {
        if (active) setCollectionOptions(options);
      })
      .catch(() => {
        if (active) setCollectionOptions([]);
      })
      .finally(() => {
        if (active) setCollectionOptionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [accessToken, fetchImpl, projectId]);

  React.useEffect(() => {
    if (state.status !== 'success') return undefined;
    let active = true;
    setDistribution({ status: 'loading' });
    fetchImpl(
      withQuery(
        `${hostname}/dashboard/${projectId}/circularity-mci-distribution`,
        collectionQuery,
      ),
      { headers: { authorization: `Bearer ${accessToken}` } },
    )
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (active) setDistribution({ status: 'success', data });
      })
      .catch(() => {
        if (active) setDistribution({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [accessToken, collectionQuery, fetchImpl, projectId, state.status]);

  React.useEffect(() => {
    if (state.status !== 'success') return undefined;
    let active = true;
    setRanking({ status: 'loading' });
    fetchImpl(
      withQuery(
        `${hostname}/dashboard/${projectId}/ranking-performance-circularity`,
        collectionQuery,
      ),
      { headers: { authorization: `Bearer ${accessToken}` } },
    )
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data.best) || !Array.isArray(data.worst)) {
          throw new Error('Invalid ranking response');
        }
        return data;
      })
      .then((data) => {
        if (active) setRanking({ status: 'success', data });
      })
      .catch(() => {
        if (active) setRanking({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [accessToken, collectionQuery, fetchImpl, projectId, state.status]);

  React.useEffect(() => {
    if (state.status !== 'success') return undefined;
    let active = true;
    setStatistics({ status: 'loading' });
    fetchImpl(
      withQuery(
        `${hostname}/dashboard/${projectId}/circularity-repairability-durability`,
        collectionQuery,
      ),
      { headers: { authorization: `Bearer ${accessToken}` } },
    )
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!data.repairability || !data.durability) {
          throw new Error('Invalid statistics response');
        }
        return data;
      })
      .then((data) => {
        if (active) setStatistics({ status: 'success', data });
      })
      .catch(() => {
        if (active) setStatistics({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [accessToken, collectionQuery, fetchImpl, projectId, state.status]);

  const collectionFilter = (
    <CollectionFilter
      loading={collectionOptionsLoading}
      options={collectionOptions}
      selected={selectedCollections}
      onChange={setSelectedCollections}
      onClear={() => setSelectedCollections([])}
    />
  );

  if (state.status === 'loading') {
    return (
      <>
        {collectionFilter}
      <Box display="flex" alignItems="center" gridGap={12} padding={3}>
        <CircularProgress size={20} />
        <Typography>Loading Circularity metrics…</Typography>
      </Box>
      </>
    );
  }

  if (state.status === 'not-calculated') {
    return (
      <Box padding={3}>
        {collectionFilter}
        <Typography>Circularity has not been calculated yet.</Typography>
      </Box>
    );
  }

  if (state.status === 'empty') {
    return (
      <Box padding={3}>
        {collectionFilter}
        <Typography>No Circularity data is available for this scope.</Typography>
      </Box>
    );
  }

  if (state.status === 'error') {
    return (
      <Box padding={3}>
        {collectionFilter}
        <Typography gutterBottom>Could not load Circularity metrics.</Typography>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => setRequestVersion((version) => version + 1)}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const { metrics, totals } = state.data;
  const mciBand = metrics.mci.band;
  const recycledContent = metrics.recycledContent || {
    percentage: null,
    totalKg: null,
    band: null,
  };
  const unrecoverableWaste = metrics.unrecoverableWaste || {
    totalKg: null,
    averageKgPerUnit: null,
  };
  const recyclability = metrics.recyclability || {
    percentage: null,
    band: null,
  };

  return (
    <Box padding={3} maxWidth={920}>
      {collectionFilter}
      <Box marginBottom={2}>
        <Typography variant="h6">Circularity metrics</Typography>
        <Typography color="textSecondary" variant="body2">
          Read-only project diagnostics from the latest persisted calculation.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Material Circularity Indicator
              </Typography>
              <Box display="flex" alignItems="center" gridGap={12}>
                <Typography variant="h4">
                  {formatNumber(metrics.mci.percentage)}%
                </Typography>
                <Chip
                  label={
                    mciBand.charAt(0).toUpperCase() + mciBand.slice(1)
                  }
                  size="small"
                  style={BAND_COLORS[mciBand]}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Recyclability index
              </Typography>
              <Box display="flex" alignItems="center" gridGap={12}>
                <Typography variant="h4">
                  {recyclability.percentage === null
                    ? 'N/A'
                    : `${formatNumber(recyclability.percentage)}%`}
                </Typography>
                {recyclability.band && (
                  <Chip
                    label={`${recyclability.band
                      .charAt(0)
                      .toUpperCase()}${recyclability.band.slice(1)}`}
                    size="small"
                    style={BAND_COLORS[recyclability.band]}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Unrecoverable waste
              </Typography>
              <Typography variant="h4">
                {unrecoverableWaste.totalKg === null
                  ? 'N/A'
                  : `${formatNumber(unrecoverableWaste.totalKg)} kg total`}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {unrecoverableWaste.averageKgPerUnit === null
                  ? 'No per-unit waste data'
                  : `${formatNumber(
                      unrecoverableWaste.averageKgPerUnit,
                    )} kg / unit`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Recycled content
              </Typography>
              <Box display="flex" alignItems="center" gridGap={12}>
                <Typography variant="h4">
                  {recycledContent.percentage === null
                    ? 'N/A'
                    : `${formatNumber(
                        recycledContent.percentage,
                      )}%`}
                </Typography>
                {recycledContent.band && (
                  <Chip
                    label={`${
                      recycledContent.band.charAt(0).toUpperCase()
                    }${recycledContent.band.slice(1)}`}
                    size="small"
                    style={BAND_COLORS[recycledContent.band]}
                  />
                )}
              </Box>
              <Typography color="textSecondary" variant="body2">
                {recycledContent.totalKg === null
                  ? 'No recycled-mass data'
                  : `${formatNumber(
                      recycledContent.totalKg,
                    )} kg recycled`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Represented articles
              </Typography>
              <Typography variant="h4">
                {formatNumber(totals.totalAmount)}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {totals.skuCount} SKU{totals.skuCount === 1 ? '' : 's'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Product mass
              </Typography>
              <Typography variant="h4">
                {formatNumber(totals.totalProductMassKg)} kg
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Box marginTop={3}>
        <Typography variant="h6" gutterBottom>
          MCI distribution
        </Typography>
        {distribution.status === 'loading' && (
          <Typography color="textSecondary">Loading distribution…</Typography>
        )}
        {distribution.status === 'error' && (
          <Typography color="textSecondary">
            MCI distribution is unavailable.
          </Typography>
        )}
        {distribution.status === 'success' && (
          <table
            aria-label="MCI distribution"
            style={{ width: '100%', borderCollapse: 'collapse' }}
          >
            <thead>
              <tr>
                <th align="left">Band</th>
                <th align="right">Amount</th>
                <th align="right">Amount percentage</th>
                <th align="right">SKU count</th>
              </tr>
            </thead>
            <tbody>
              {distribution.data.buckets.map((bucket) => (
                <tr key={bucket.band}>
                  <td style={{ padding: '8px 0' }}>
                    {bucket.band.charAt(0).toUpperCase() + bucket.band.slice(1)}
                  </td>
                  <td align="right">{formatNumber(bucket.amount)}</td>
                  <td align="right">
                    {formatNumber(bucket.amountPercentage)}%
                  </td>
                  <td align="right">{bucket.skuCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Box>
      <Box marginTop={3}>
        <Typography variant="h6" gutterBottom>
          Repairability and durability
        </Typography>
        {statistics.status === 'loading' && (
          <Typography color="textSecondary">Loading statistics…</Typography>
        )}
        {statistics.status === 'error' && (
          <Typography color="textSecondary">
            Repairability and durability statistics are unavailable.
          </Typography>
        )}
        {statistics.status === 'success' && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1">Repairability (0–6)</Typography>
              <CircularityStatisticsTable
                label="Repairability"
                statistics={statistics.data.repairability}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1">Durability (0–1)</Typography>
              <CircularityStatisticsTable
                label="Durability"
                statistics={statistics.data.durability}
              />
            </Grid>
          </Grid>
        )}
      </Box>
      <Box marginTop={3}>
        <Typography variant="h6" gutterBottom>
          Overall circularity performance
        </Typography>
        {ranking.status === 'loading' && (
          <Typography color="textSecondary">Loading ranking…</Typography>
        )}
        {ranking.status === 'error' && (
          <Typography color="textSecondary">
            Circularity ranking is unavailable.
          </Typography>
        )}
        {ranking.status === 'success' && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1">Best five</Typography>
              <CircularityRankingTable label="Best" rows={ranking.data.best} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1">Worst five</Typography>
              <CircularityRankingTable
                label="Worst"
                rows={ranking.data.worst}
              />
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default CircularityMetricsSection;
